import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthContext';
import MetricCard from '../../../components/MetricCard/MetricCard';
import { useDashboardData } from '../../../hooks/useDashboardData';
import styles from './DashboardHome.module.css';

const DashboardHome: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { recentTransactions, recentActivity, loading, error } = useDashboardData();

  // Format currency
  const formatCurrency = (value: number): string => {
    return `₹${value.toLocaleString('en-IN')}`;
  };

  // Module to permission name mapping
  const modulePermissionMap: { [key: string]: string } = {
    'Party Master': 'Party Master',
    'Party Accounts': 'Party Accounts',
    'Token Master': 'Token Master',
    'Dollar Account': 'Dollar Account',
    'Internal Account': 'Internal Account',
    'Payment Modes': 'Payment Mode',
    'Buy': 'Buy',
    'Sell': 'Sell',
    'Swap': 'Swap',
    'Ledger': 'Ledger',
    'Daily Closing': 'Daily Closing',
    'Settlement': 'Reports',
    'Balance': 'Reports',
    'P&L Report': 'Reports',
    'Sale Report': 'Reports',
    'Purchase': 'Reports',
    'Tax History': 'Reports',
    'Users': 'Users',
    'Audit Log': 'Audit Log',
  };

  // Check if user has permission to view a module
  const hasModulePermission = (moduleName: string): boolean => {
    const permissionName = modulePermissionMap[moduleName];
    if (!permissionName) {
      return false; // Hide if no permission mapping
    }

    if (!user?.userPermissionList || user.userPermissionList.length === 0) {
      return false; // Hide if no permissions
    }

    const permission = user.userPermissionList.find(
      (p) => p.moduleName?.toLowerCase() === permissionName.toLowerCase()
    );

    console.log(`Module: ${moduleName} -> Permission: ${permissionName} -> isView: ${permission?.isView}`);
    return permission?.isView === true;
  };

  // Quick action buttons - matching screenshot order
  const quickActions = [
    { label: 'Add Buy', color: 'green', icon: '🛒', path: '/dashboard/buy', permissionModule: 'Buy' },
    { label: 'Add Sell', color: 'red', icon: '❤️', path: '/dashboard/sell', permissionModule: 'Sell' },
    { label: 'Add Swap', color: 'yellow', icon: '⇄', path: '/dashboard/swap', permissionModule: 'Swap' },
    { label: 'Add Party', color: 'blue', icon: '👥', path: '/dashboard/party-master', permissionModule: 'Party Master' },
    { label: 'Reports', color: 'purple', icon: '📊', path: '/dashboard/reports', permissionModule: 'Reports' },
  ];

  // Helper function to check if user has permission for a specific module by permission name
  const hasPermissionByName = (permissionName: string): boolean => {
    if (!user?.userPermissionList || user.userPermissionList.length === 0) {
      return false;
    }

    const permission = user.userPermissionList.find(
      (p) => p.moduleName?.toLowerCase() === permissionName.toLowerCase()
    );

    return permission?.isView === true;
  };

  // All modules - matching screenshot exactly with correct icons and colors
  const modules = [
    { label: 'Party Master', icon: '👥', color: '#3b82f6', path: '/dashboard/party-master' },
    { label: 'Party Accounts', icon: '📋', color: '#a78bfa', path: '/dashboard/party-accounts' },
    { label: 'Token Master', icon: '🎫', color: '#fbbf24', path: '/dashboard/token-master' },
    { label: 'Dollar Account', icon: '💵', color: '#10b981', path: '/dashboard/dollar-accounts' },
    { label: 'Internal Account', icon: '🏛️', color: '#a78bfa', path: '/dashboard/internal-accounts' },
    { label: 'Payment Modes', icon: '💳', color: '06b6d4', path: '/dashboard/payment-modes' },
    { label: 'Buy', icon: '🛒', color: '#10b981', path: '/dashboard/buy' },
    { label: 'Sell', icon: '❤️', color: '#f87171', path: '/dashboard/sell' },
    { label: 'Swap', icon: '🔄', color: '#fbbf24', path: '/dashboard/swap' },
    { label: 'Ledger', icon: '📖', color: '#60a5fa', path: '/dashboard/ledger' },
    { label: 'Daily Closing', icon: '📋', color: '#fb923c', path: '/dashboard/daily-closing' },
    { label: 'Settlement', icon: '⚖️', color: '06b6d4', path: '/dashboard/settlement' },
    { label: 'Balance', icon: '💰', color: '#3b82f6', path: '/dashboard/balance' },
    { label: 'P&L Report', icon: '📊', color: '#f87171', path: '/dashboard/pl-report' },
    { label: 'Sale Report', icon: '📄', color: '#ec4899', path: '/dashboard/sale-report' },
    { label: 'Purchase', icon: '📦', color: '#86efac', path: '/dashboard/purchase' },
    { label: 'Tax History', icon: '📋', color: '#a78bfa', path: '/dashboard/tax-history' },
    { label: 'Users', icon: '👤', color: '#d8b4fe', path: '/dashboard/users' },
    { label: 'Audit Log', icon: '📝', color: '#60a5fa', path: '/dashboard/audit-logs' },
  ];

  // Memoize metric cards data - using default values since metrics API is removed
  const metricCardsData = useMemo(() => {
    return [
      { icon: '👥', label: 'PARTIES', value: '0', subtitle: '0 active', color: 'gray' as const },
      { icon: '🎫', label: 'TOKENS', value: '0', color: 'gray' as const },
      { icon: '📥', label: 'TODAY BUY', value: '₹0', subtitle: '0 trans', color: 'green' as const },
      { icon: '📤', label: 'TODAY SELL', value: '₹0', subtitle: '0 trans', color: 'red' as const },
      { icon: '🏦', label: 'ACCOUNTS', value: '0', color: 'gray' as const },
      { icon: '🔄', label: 'SWAPS', value: '0', subtitle: 'today', color: 'yellow' as const },
    ];
  }, []);

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Dashboard</h1>
          <p className={styles.subtitle}>Overview of your account management system</p>
        </div>
        <div className={styles.errorMessage}>Error: {error}</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Page Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Dashboard</h1>
        <p className={styles.subtitle}>Business Account Management</p>
      </div>

      {/* Search Bar */}
      <div className={styles.searchBar}>
        <span className={styles.searchIcon}>🔍</span>
        <input
          type="text"
          placeholder="Search modules, parties, tokens..."
          className={styles.searchInput}
        />
      </div>

      {/* Quick Action Buttons */}
      <div className={styles.quickActionsContainer}>
        {quickActions
          .filter((action) => hasPermissionByName(action.permissionModule))
          .map((action, index) => (
            <button
              key={index}
              className={`${styles.quickActionBtn} ${styles[action.color]}`}
              onClick={() => navigate(action.path)}
            >
              {action.icon} {action.label}
            </button>
          ))}
      </div>

      {/* Metric Cards Grid */}
      <div className={styles.metricsGrid}>
        {metricCardsData.map((card, index) => (
          <MetricCard
            key={index}
            icon={card.icon}
            label={card.label}
            value={card.value}
            subtitle={card.subtitle}
            color={card.color}
          />
        ))}
      </div>

      {/* All Modules Section */}
      <div className={styles.modulesSection}>
        <h2 className={styles.sectionTitle}>ALL MODULES</h2>
        <div className={styles.modulesGrid}>
          {modules
            .filter((module) => hasModulePermission(module.label))
            .map((module, index) => (
              <div
                key={index}
                className={styles.moduleCard}
                onClick={() => navigate(module.path)}
              >
                <div className={styles.moduleIcon} style={{ color: module.color }}>
                  {module.icon}
                </div>
                <div className={styles.moduleLabel}>{module.label}</div>
              </div>
            ))}
        </div>
      </div>

      {/* Two-Column Section for Recent Transactions and Activity */}
      <div className={styles.sectionsContainer}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Recent Transactions</h2>
          {loading ? (
            <div className={styles.emptyState}>Loading...</div>
          ) : recentTransactions.length === 0 ? (
            <div className={styles.emptyState}>No recent transactions</div>
          ) : (
            <div className={styles.transactionsList}>
              {recentTransactions.map((transaction) => {
                // Determine icon and color based on transaction type
                let icon = '⇄';
                let iconColor = '#fbbf24';
                let badgeColor = 'yellow';
                
                if (transaction.transactionType?.toLowerCase().includes('buy')) {
                  icon = '📈';
                  iconColor = '#10b981';
                  badgeColor = 'green';
                } else if (transaction.transactionType?.toLowerCase().includes('sell')) {
                  icon = '📉';
                  iconColor = '#ef4444';
                  badgeColor = 'red';
                } else if (transaction.transactionType?.toLowerCase().includes('swap')) {
                  icon = '⇄';
                  iconColor = '#fbbf24';
                  badgeColor = 'yellow';
                }

                return (
                  <div key={transaction.id} className={styles.transactionItem}>
                    <div className={styles.transactionIcon} style={{ color: iconColor }}>
                      {icon}
                    </div>
                    <div className={styles.transactionDetails}>
                      <div className={styles.transactionParty}>{transaction.partyName || ''}</div>
                      <div className={styles.transactionDate}>{transaction.transactionDate || ''}</div>
                    </div>
                    <div className={styles.transactionRight}>
                      <div className={styles.transactionAmount}>{formatCurrency(transaction.amount)}</div>
                      <span className={`${styles.transactionBadge} ${styles[`badge${badgeColor.charAt(0).toUpperCase() + badgeColor.slice(1)}`]}`}>
                        {transaction.transactionType?.toLowerCase() || ''}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Recent Activity</h2>
          {loading ? (
            <div className={styles.emptyState}>Loading...</div>
          ) : recentActivity.length === 0 ? (
            <div className={styles.emptyState}>No recent activity</div>
          ) : (
            <div className={styles.activityList}>
              {recentActivity.map((activity) => (
                <div key={activity.id} className={styles.activityItem}>
                  <div className={styles.activityBullet}>●</div>
                  <div className={styles.activityDetails}>
                    <div className={styles.activityAction}>{activity.creatorName} {activity.remarks}</div>
                    <div className={styles.activityTime}>{activity.createdDate}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
